import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ProfileCardSliderContext } from './ProfileCardSliderContext';

const SLIDE_DURATION = '0.35s';
const SLIDE_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

const styles = cssMap({
	viewport: {
		position: 'relative',
		overflowX: 'hidden',
		overflowY: 'hidden',
		width: '100%',
	},
	pane: {
		transitionProperty: 'transform',
		transitionDuration: SLIDE_DURATION,
		transitionTimingFunction: SLIDE_EASING,
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0.001s',
		},
	},
	frontShown: {
		transform: 'translateX(0)',
	},
	frontHidden: {
		transform: 'translateX(-100%)',
	},
	backPane: {
		position: 'absolute',
		inset: token('space.0'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	backHidden: {
		transform: 'translateX(100%)',
	},
	backShown: {
		transform: 'translateX(0)',
	},
});

function useInert(active: boolean) {
	const ref = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const node = ref.current;
		if (!node) {
			return;
		}
		if (active) {
			node.removeAttribute('inert');
		} else {
			node.setAttribute('inert', '');
		}
	}, [active]);
	return ref;
}

export interface ProfileCardSliderProviderProps {
	/** Card content (header + body) that reads the slider state via context. */
	children: React.ReactNode;
	/** Fired when the reporting-line pane is revealed. */
	onView?: () => void;
	/** Fired when returning to the profile pane. */
	onBack?: () => void;
}

/**
 * Holds the reporting-line slide state and exposes it via {@link ProfileCardSliderContext} so the
 * trigger (on the Manager row) and the sliding panes (in the card body) share one source of truth.
 */
export function ProfileCardSliderProvider({
	children,
	onView,
	onBack,
}: ProfileCardSliderProviderProps): React.JSX.Element {
	const [showBack, setShowBack] = useState(false);
	const [resetToken, setResetToken] = useState(0);
	const backPaneId = useId();

	const handleView = useCallback(() => {
		setShowBack(true);
		setResetToken((token) => token + 1);
		onView?.();
	}, [onView]);

	const handleBack = useCallback(() => {
		setShowBack(false);
		onBack?.();
	}, [onBack]);

	const value = useMemo(
		() => ({ showBack, onView: handleView, onBack: handleBack, backPaneId, resetToken }),
		[showBack, handleView, handleBack, backPaneId, resetToken],
	);

	return (
		<ProfileCardSliderContext.Provider value={value}>{children}</ProfileCardSliderContext.Provider>
	);
}

export interface ProfileCardSliderPanesProps {
	/** Pane shown by default */
	front: React.ReactNode;
	/**
	 * Pane revealed when the user drills. Receives a `back` callback to return
	 * to the front pane and a `ref` to move focus to on entry.
	 */
	back: (args: {
		back: () => void;
		headingRef: React.RefObject<HTMLDivElement>;
	}) => React.ReactNode;
	/** Current shown/hidden state (from {@link ProfileCardSliderContext}). */
	showBack: boolean;
	/** Return-to-profile callback (from {@link ProfileCardSliderContext}). */
	onBack: () => void;
	/** DOM id applied to the reporting-line region (from {@link ProfileCardSliderContext}). */
	backPaneId: string;
	testId?: string;
}

/**
 * The two-pane viewport. The front pane (profile / recent work) slides left as the back pane
 * (reporting line) slides in from the right. The front pane stays in flow and sizes the card; the
 * back pane is an absolute overlay that fills it. Honors `prefers-reduced-motion` and manages
 * `inert` + focus so keyboard and screen-reader users only interact with the visible pane.
 */
export function ProfileCardSliderPanes({
	front,
	back,
	showBack,
	onBack,
	backPaneId,
	testId,
}: ProfileCardSliderPanesProps): React.JSX.Element {
	const backHeadingRef = useRef<HTMLDivElement>(null);
	const viewportRef = useRef<HTMLDivElement | null>(null);

	const frontInertRef = useInert(!showBack);
	const backInertRef = useInert(showBack);

	useEffect(() => {
		if (showBack) {
			backHeadingRef.current?.focus({ preventScroll: true });
		}
	}, [showBack]);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) {
			return;
		}
		viewport.scrollLeft = 0;
		viewport.scrollTop = 0;
	}, [showBack]);

	return (
		<Box ref={viewportRef} xcss={styles.viewport} testId={testId}>
			<Box
				ref={frontInertRef}
				xcss={cx(styles.pane, showBack ? styles.frontHidden : styles.frontShown)}
			>
				{front}
			</Box>
			<Box
				ref={backInertRef}
				id={backPaneId}
				role="group"
				xcss={cx(styles.pane, styles.backPane, showBack ? styles.backShown : styles.backHidden)}
			>
				{back({ back: onBack, headingRef: backHeadingRef })}
			</Box>
		</Box>
	);
}
